import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import { AnimatePresence } from '@sopranodesign/framer-motion';
import {
  Flex,
  Modal,
  Table,
  Button,
  Checkbox,
  TableRow,
  Paragraph,
  TableBody,
  TableCell,
  TableHead,
  Pagination,
  PanelSlider,
  TableActions,
  useNotification,
  LoadingContainer,
  OrderableTableRow,
  usePaginationReducer,
  useOrderableTableReducer
} from '@sopranodesign/pulse-components';
import {
  useQuery,
  useQueryClient
} from '@sopranodesign/pulse-hooks';
import {
  useAuth,
  PermissionFilter
} from '@sopranodesign/pulse-auth';
import Icon from '@sopranodesign/pulse-icons';

import {
  fetchJiraIssues,
  recrawlJiraIssue,
  deleteJiraIssue,
  createJiraIssueTag,
  fetchJiraIssueTags,
  saveJiraIssueTags,
  fetchJiraIssueAvailableTags
} from '../api';
import { StateLevel } from '.';
import WarningMessageBody from './WarningMessageBody';
import { useTagManager } from './useTagManager';
import { useBulkSelection } from './useBulkSelection';
import { runBulkAction, describeBulkResult } from './bulkActionUtils';
import BulkActionsBar from './BulkActionsBar';
import TagModal from './TagModal';

const JiraIssuesList = ({
  projectId,
  handleHide,
  onOpenTagsModal,
  onOpenRecrawlModal,
  loadingIds,
  errorIds
}) => {
  const { hasPermission } = useAuth();
  const canViewTag = hasPermission('view generative ai tag');
  const canEditTag = hasPermission('edit generative ai tag');
  const canEditResource = hasPermission('edit generative ai resource');
  const canBulkSelect = canEditResource || (canViewTag && canEditTag);

  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  const [shouldRefetch, setShouldRefetch] = useState(false);

  const bulk = useBulkSelection();
  const [bulkModal, setBulkModal] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [isBulkTagsModalVisible, setIsBulkTagsModalVisible] = useState(false);

  const order = useOrderableTableReducer({ id: `jira-${projectId}` });
  const pagination = usePaginationReducer({ id: `jira-${projectId}` });

  const { data, isLoading } = useQuery({
    queryKey: [
      'generative-ai/jira/issues',
      projectId,
      { ...pagination.params, ...order.params }
    ],
    queryFn: () => fetchJiraIssues(projectId, { ...pagination.params, ...order.params }),
    keepPreviousData: true,
    refetchInterval: shouldRefetch ? 5000 : false
  });

  const { data: availableTagsData } = useQuery({
    queryKey: ['generative-ai/jira/groups/available'],
    queryFn: fetchJiraIssueAvailableTags,
    staleTime: 5 * 60 * 1000,
    enabled: canViewTag
  });

  const { tags, selectedTags, setSelectedTags, handleTagChange, setTags } = useTagManager({
    scope: 'generative_ai_jira_issues',
    createTagFn: createJiraIssueTag
  });

  const response = useMemo(() => data?.data ?? {}, [data]);
  const issues = useMemo(() => response.data ?? [], [response]);
  const meta = response.meta ?? {};
  const projectName = response.project_name ?? 'Issues';
  const issueIds = useMemo(() => issues.map(issue => issue.id), [issues]);

  const globalHasPending = response.has_pending ?? false;

  useEffect(() => {
    setShouldRefetch(globalHasPending);
  }, [globalHasPending]);

  const refetchIssues = () => {
    queryClient.invalidateQueries({ queryKey: ['generative-ai/jira/issues', projectId] });
  };

  const findIssue = (id) => issues.find(issue => issue.id === id);

  const handleBulkRecrawl = () => {
    const ids = bulk.selectedIds;
    if (ids.length === 0) {
      return;
    }
    setBulkModal({
      header: 'Confirm Recrawl',
      body: (
        <WarningMessageBody
          contentHeader={`Are you sure you want to recrawl ${ids.length} selected issue${ids.length > 1 ? 's' : ''}?`}
          contentBody="This will update their content."
        />
      ),
      confirmText: 'Recrawl',
      handleConfirm: async () => {
        setBulkLoading(true);
        try {
          const result = await runBulkAction(ids, recrawlJiraIssue);
          if (result.succeeded.length > 0) {
            showSuccess(`${describeBulkResult(result, 'recrawl requested', 'issue')}.`);
          }
          if (result.failed.length > 0) {
            showError(`Failed to request recrawl for ${result.failed.length} issue${result.failed.length > 1 ? 's' : ''}.`);
          }
          bulk.clear();
        } finally {
          setBulkLoading(false);
          setBulkModal(null);
          refetchIssues();
        }
      }
    });
  };

  const handleBulkDelete = () => {
    const eligibleIds = bulk.selectedIds.filter(id => findIssue(id)?.status !== 'Deleting');
    if (eligibleIds.length === 0) {
      showError('None of the selected issues can be deleted right now.');
      return;
    }
    setBulkModal({
      header: 'Confirm Delete',
      body: (
        <WarningMessageBody
          contentHeader={`Are you sure you want to delete ${eligibleIds.length} selected issue${eligibleIds.length > 1 ? 's' : ''}?`}
          contentBody="All synced data will be permanently removed."
          warningText="This action cannot be undone."
        />
      ),
      confirmText: 'Delete',
      handleConfirm: async () => {
        setBulkLoading(true);
        try {
          const result = await runBulkAction(eligibleIds, deleteJiraIssue);
          if (result.succeeded.length > 0) {
            showSuccess(`${describeBulkResult(result, 'delete requested', 'issue')}.`);
          }
          if (result.failed.length > 0) {
            showError(`Failed to request delete for ${result.failed.length} issue${result.failed.length > 1 ? 's' : ''}.`);
          }
          bulk.clear();
        } finally {
          setBulkLoading(false);
          setBulkModal(null);
          refetchIssues();
        }
      }
    });
  };

  const openBulkTagsModal = () => {
    const eligibleIds = bulk.selectedIds.filter(id => findIssue(id)?.status === 'Success');
    if (eligibleIds.length === 0) {
      showError('None of the selected issues are ready to be tagged.');
      return;
    }
    const fetchedTags = availableTagsData?.data?.data ?? [];
    setTags(fetchedTags);
    setSelectedTags([]);
    setIsBulkTagsModalVisible(true);
  };

  const handleBulkTagConfirm = async () => {
    const eligibleIds = bulk.selectedIds.filter(id => findIssue(id)?.status === 'Success');
    setBulkLoading(true);
    try {
      const result = await runBulkAction(eligibleIds, async (id) => {
        const assignedResponse = await fetchJiraIssueTags(id);
        const currentTagIds = (assignedResponse.data.data ?? []).map(tag => tag.id);
        const mergedTagIds = Array.from(new Set([...currentTagIds, ...selectedTags]));
        return saveJiraIssueTags({ issueId: id, groupIds: mergedTagIds });
      });
      if (result.succeeded.length > 0) {
        showSuccess(`${describeBulkResult(result, 'tagged', 'issue')}.`);
      }
      if (result.failed.length > 0) {
        showError(`Failed to tag ${result.failed.length} issue${result.failed.length > 1 ? 's' : ''}.`);
      }
      bulk.clear();
      refetchIssues();
    } finally {
      setBulkLoading(false);
      setIsBulkTagsModalVisible(false);
      setSelectedTags([]);
    }
  };

  return (
    <AnimatePresence>
      <PanelSlider header={projectName} handleHide={handleHide}>
        {isLoading && <LoadingContainer />}
        {!isLoading && !issues.length && (
          <Paragraph mb secondary>No issues available.</Paragraph>
        )}
        {!isLoading && issues.length > 0 && (
          <Flex col>
            <PermissionFilter can="edit generative ai resource">
              <BulkActionsBar
                count={bulk.count}
                onClear={bulk.clear}
                actions={[
                  <PermissionFilter can="view generative ai tag" key="bulk-tag">
                    <PermissionFilter can="edit generative ai tag">
                      <Button
                        icon="tag"
                        variant="secondary"
                        onClick={openBulkTagsModal}
                      >
                        Tags
                      </Button>
                    </PermissionFilter>
                  </PermissionFilter>,
                  <Button
                    key="bulk-recrawl"
                    icon="download"
                    variant="secondary"
                    loading={bulkLoading}
                    onClick={handleBulkRecrawl}
                  >
                    Recrawl
                  </Button>,
                  <Button
                    key="bulk-delete"
                    variant="secondary"
                    icon="trash"
                    colour="danger"
                    loading={bulkLoading}
                    onClick={handleBulkDelete}
                  >
                    Delete
                  </Button>
                ]}
              />
            </PermissionFilter>
            <Flex border xScroll>
              <Table>
                <TableHead>
                  <OrderableTableRow
                    rows={[
                      ...(canBulkSelect ? [{
                        label: (
                          <Checkbox
                            aria-label="Select all issues"
                            checked={bulk.isAllSelected(issueIds)}
                            onChange={() => bulk.toggleAll(issueIds)}
                          />
                        )
                      }] : []),
                      { label: 'Issue', sort: 'summary' },
                      { label: 'Status', sort: 'status' },
                      { label: 'Crawled At', sort: 'updated_at' },
                      ...(canViewTag ? [{ label: 'Tags' }] : []),
                      ...(((canViewTag && canEditTag) || canEditResource) ? [null] : [])
                    ]}
                    {...order.props}
                  />
                </TableHead>
                <TableBody>
                  {issues.map(issue => (
                    <TableRow key={issue.id}>
                      {canBulkSelect && (
                        <TableCell>
                          <Checkbox
                            aria-label={`Select ${issue.issue_key}`}
                            checked={bulk.isSelected(issue.id)}
                            onChange={() => bulk.toggle(issue.id)}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <Flex center>
                          <span>{issue.issue_key} — {issue.summary}</span>
                          {issue.attachment_count > 0 && (
                            <Flex ml center title={`${issue.attachment_count} attachment(s)`}>
                              <Icon type="document" size="1rem" width="1rem" height="1rem" />
                              <span>{issue.attachment_count}</span>
                            </Flex>
                          )}
                        </Flex>
                      </TableCell>
                      <TableCell>
                        <Flex center>
                          <StateLevel level={issue.status} />
                          <span>{issue.status}</span>
                        </Flex>
                      </TableCell>
                      <TableCell>{dayjs(issue.updated_at).format('YYYY-MM-DD HH:mm:ss')}</TableCell>

                      <PermissionFilter can="view generative ai tag">
                        <TableCell>
                          {issue.groups && issue.groups.length > 0
                            ? issue.groups.map(group => group.name).join(', ')
                            : 'No Tags'}
                        </TableCell>
                      </PermissionFilter>

                      {((canViewTag && canEditTag) || canEditResource) && (
                        <TableActions>
                          <PermissionFilter can="view generative ai tag">
                            <PermissionFilter can="edit generative ai tag">
                              {issue.status === 'Success' && (
                                <Button
                                  icon="tag"
                                  onClick={() => onOpenTagsModal(issue)}
                                >
                                  Tags
                                </Button>
                              )}
                            </PermissionFilter>
                          </PermissionFilter>

                          <PermissionFilter can="edit generative ai resource">
                            {issue.status !== 'Deleting' && (
                              <Button
                                colour="danger"
                                icon="trash"
                                loading={Boolean(loadingIds?.[issue.id])}
                                onClick={async () => {
                                  try {
                                    await deleteJiraIssue(issue.id);
                                    showSuccess('Issue delete requested');
                                    refetchIssues();
                                  } catch (error) {
                                    showError(`Failed to delete issue: ${error.message}`);
                                  }
                                }}
                              >
                                Delete
                              </Button>
                            )}
                            <Button
                              icon="download"
                              loading={Boolean(loadingIds?.[issue.id])}
                              onClick={() => onOpenRecrawlModal(issue)}
                            >
                              Recrawl
                            </Button>
                          </PermissionFilter>

                          {errorIds?.[issue.id] && (
                            <p style={{ color: 'red', marginTop: '4px' }}>
                              {errorIds[issue.id]}
                            </p>
                          )}
                        </TableActions>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Flex>

            {typeof meta.current_page === 'number' && (
              <Pagination pagination={meta} {...pagination.props} />
            )}

            <TagModal
              isVisible={isBulkTagsModalVisible}
              header={`Tag ${bulk.count} Issues`}
              onCancel={() => {
                setIsBulkTagsModalVisible(false);
                setSelectedTags([]);
              }}
              onConfirm={handleBulkTagConfirm}
              tags={tags}
              selectedTags={selectedTags}
              handleTagChange={handleTagChange}
            />

            {bulkModal && (
              <Modal
                header={bulkModal.header}
                body={bulkModal.body}
                confirmText={bulkModal.confirmText}
                cancelText="Cancel"
                handleCancel={() => setBulkModal(null)}
                handleConfirm={bulkModal.handleConfirm}
                loading={bulkLoading}
              />
            )}
          </Flex>
        )}
      </PanelSlider>
    </AnimatePresence>
  );
};

JiraIssuesList.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  handleHide: PropTypes.func.isRequired,
  onOpenTagsModal: PropTypes.func.isRequired,
  onOpenRecrawlModal: PropTypes.func.isRequired,
  loadingIds: PropTypes.object,
  errorIds: PropTypes.object
};


export default JiraIssuesList;
